import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from '../../../hooks/useTranslation';

const WeekOne = ({ onBack, onNext, onProgressUpdate, onReflectionUpdate, initialCompletedActivities = [], initialReflectionAnswers = {}, friend }) => {
  const [completedActivities, setCompletedActivities] = useState(initialCompletedActivities);
  const [currentActivityIndex, setCurrentActivityIndex] = useState(0);
  const [reflectionAnswers, setReflectionAnswers] = useState({});
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const isInitialMount = useRef(true);
  const prevInitialActivities = useRef(initialCompletedActivities);

  const activities = [
    {
      id: 'fit-check',
      type: 'exercise',
      title: 'Step 1: Friendship Fit & Safety Check',
      description: 'Take 3 quiet minutes before reaching out',
      content: `**STEP 1: FRIENDSHIP FIT & SAFETY CHECK**

Take 3 quiet minutes before reaching out, and answer honestly as you consider each point below.

- Did this friendship feel mostly respectful and mutual?

- Do I feel emotionally safe reconnecting with ${friend?.name || 'your friend'}?

- Am I reaching out to build connection — not to seek validation or closure?

- If ${friend?.name || 'your friend'} doesn't respond, will I still respect myself for trying?

Choose your path:

- Rebuild: Good friendship, natural drift

- Repair: Something happened, needs care

- Release: Not safe or not mutual (you can still complete this journey inwardly)

This step protects your energy and your dignity.`,
      estimatedTime: '3 minutes'
    },
    {
      id: 'intention',
      type: 'exercise',
      title: 'Step 2: Clarify Your Intention',
      description: 'Reflection (10–15 minutes)',
      content: `**STEP 2: CLARIFY YOUR INTENTION**


- What do I genuinely appreciate about ${friend?.name || 'your friend'}?

- What kind of connection do I realistically want right now? (e.g. occasional catch-ups, a light weekly rhythm, deeper emotional closeness)

- What would a "good enough" outcome be — even if we don't fully reconnect?

- What memory represents the best of our friendship?

Write freely. This is for you only.`,
      estimatedTime: '10-15 minutes'
    },
    {
      id: 'craft-message',
      type: 'exercise',
      title: 'Step 3: Craft a Low-Pressure Message',
      description: 'Choose ONE version and personalize it',
      content: `**STEP 3: CRAFT A LOW-PRESSURE MESSAGE**

Choose ONE version and personalize it.

Option A — Light & Easy:

"Hey ${friend?.name || '[Name]'} — I was thinking about [specific memory] and it made me smile. How are you these days? If you're up for it, I'd love to catch up sometime."

Option B — Slightly Deeper:

"Hey ${friend?.name || '[Name]'} — I've been thinking about you lately and realized I miss our connection. No pressure at all, but I'd really enjoy catching up if you're open to it."

Guidelines:

- Be specific, not long

- No apologies for reaching out

- No emotional weight or expectations

- One invitation, not a life update`,
      estimatedTime: '15 minutes'
    },
    {
      id: 'send',
      type: 'challenge',
      title: 'Step 4: Send the Message',
      description: "Today's challenge: Send it",
      content: `**STEP 4: SEND THE MESSAGE**

Today's challenge: Send it.

Before sending:

- Read it out loud

- Remove anything that feels needy or heavy

After sending:

- Take 5 slow breaths

- Say (out loud or in writing):
  "I showed up with courage and integrity."

Important:

- Do not re-read the message repeatedly

- Do not check for replies more than twice today

- Response timing is not a reflection of your value

Success = sending. Nothing else.`,
      estimatedTime: '5 minutes'
    },
    {
      id: 'vulnerability',
      type: 'exercise',
      title: 'Step 5: The Vulnerability Hangover',
      description: 'What you may feel next (all normal)',
      content: `**STEP 5: THE VULNERABILITY HANGOVER**

What you may feel next (all normal):

- Restlessness

- Overthinking

- Urge to check your phone

- Self-doubt

This is your nervous system — not reality.

Do one grounding action now:

- Walk for 5 minutes

- Drink a glass of water slowly

- Put your phone away for 30 minutes

Reminder:

Reaching out is an act of generosity, not a transaction.`,
      estimatedTime: '10 minutes'
    },
    {
      id: 'reflection',
      type: 'exercise',
      title: 'Week 1 Reflection',
      description: 'Answer briefly',
      content: `**WEEK 1 REFLECTION**

Answer briefly:

- What did it take for me to send that message?

- What am I proud of right now?

- If a close friend did this, what would I say to them?`,
      estimatedTime: '10 minutes'
    }
  ];

  // Load completed activities and reflection answers from localStorage
  useEffect(() => {
    // Only sync from initialCompletedActivities on initial mount
    // This prevents resetting local state when user is actively checking/unchecking
    if (isInitialMount.current) {
      if (currentUser) {
        setCompletedActivities(initialCompletedActivities || []);
        prevInitialActivities.current = initialCompletedActivities;
      } else {
        // Fallback to localStorage for non-authenticated users
        const saved = localStorage.getItem('week1CompletedActivities');
        if (saved) {
          setCompletedActivities(JSON.parse(saved));
        }
      }
      
      // Reflection answers will be loaded when activity changes
      
      isInitialMount.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Set current activity to first incomplete activity on mount
  useEffect(() => {
    const firstIncompleteIndex = activities.findIndex(
      activity => !completedActivities.includes(activity.id)
    );
    if (firstIncompleteIndex !== -1) {
      setCurrentActivityIndex(firstIncompleteIndex);
    } else {
      // All activities completed, show the last one
      setCurrentActivityIndex(activities.length - 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load reflection answers when activity changes
  useEffect(() => {
    if (currentActivityIndex >= 0 && activities[currentActivityIndex]) {
      const activity = activities[currentActivityIndex];
      
      // Use initialReflectionAnswers from props (Firebase) if available, otherwise fallback to localStorage
      if (Object.keys(initialReflectionAnswers).length > 0) {
        setReflectionAnswers(initialReflectionAnswers);
      } else {
        const savedAnswers = localStorage.getItem(`week1Reflections_${activity.id}`);
        if (savedAnswers) {
          setReflectionAnswers(JSON.parse(savedAnswers));
        } else {
          setReflectionAnswers({});
        }
      }
    }
  }, [currentActivityIndex, initialReflectionAnswers]);

  const handleReflectionChange = (questionKey, value) => {
    const newAnswers = {
      ...reflectionAnswers,
      [questionKey]: value
    };
    setReflectionAnswers(newAnswers);
    
    // Save to Firebase if callback provided, otherwise save to localStorage
    if (onReflectionUpdate) {
      onReflectionUpdate(newAnswers);
    } else {
      // Fallback to localStorage for non-authenticated users
      if (currentActivityIndex >= 0 && activities[currentActivityIndex]) {
        const activity = activities[currentActivityIndex];
        localStorage.setItem(`week1Reflections_${activity.id}`, JSON.stringify(newAnswers));
      }
    }
  };

  // Check if a line is a question/reflection prompt
  const isQuestion = (line) => {
    const trimmed = line.trim();
    return (
      trimmed.includes('?') ||
      trimmed.toLowerCase().startsWith('answer') ||
      trimmed.toLowerCase().startsWith('reflect') ||
      (trimmed.startsWith('-') && (trimmed.includes('?') || trimmed.toLowerCase().includes('what') || trimmed.toLowerCase().includes('how')))
    );
  };

  const toggleActivity = (activityId) => {
    setCompletedActivities(prevCompleted => {
      const newCompleted = prevCompleted.includes(activityId)
        ? prevCompleted.filter(id => id !== activityId)
        : [...prevCompleted, activityId];
      
      // Save to localStorage for non-authenticated users
      if (!currentUser) {
        localStorage.setItem('week1CompletedActivities', JSON.stringify(newCompleted));
      }
      // Always pass the array - handleProgressUpdate will calculate the length
      onProgressUpdate(newCompleted);
      
      return newCompleted;
    });
  };

  const handleNextActivity = () => {
    if (currentActivityIndex < activities.length - 1) {
      setCurrentActivityIndex(currentActivityIndex + 1);
    }
  };

  const handlePreviousActivity = () => {
    if (currentActivityIndex > 0) {
      setCurrentActivityIndex(currentActivityIndex - 1);
    }
  };

  const currentActivity = activities[currentActivityIndex];
  const isCurrentActivityCompleted = completedActivities.includes(currentActivity?.id);
  const canGoNext = currentActivityIndex < activities.length - 1;
  const canGoPrevious = currentActivityIndex > 0;
  const allActivitiesCompleted = completedActivities.length === activities.length;

  const getActivityIcon = (type) => {
    switch (type) {
      case 'video': return '🎥';
      case 'article': return '📖';
      case 'exercise': return '✏️';
      case 'challenge': return '🎯';
      default: return '📝';
    }
  };

  return (
    <div className="week-container">
      <div className="week-header">
        <button className="back-btn" onClick={onBack}>
          ← Back to Overview
        </button>
        <div className="week-title">
          <h1>Week 1: Prepare & Reach Out</h1>
          <p><strong>Goal:</strong> Choose the right friendship, set healthy expectations, and send one grounded reconnection message.</p>
        </div>
        <div className="week-progress">
          <span className="progress-text">
            {completedActivities.length}/{activities.length} {t('activitiesCompleted')}
          </span>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${(completedActivities.length / activities.length) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>

      <div className="activity-progress-indicator">
        <span className="activity-counter">
          Activity {currentActivityIndex + 1} of {activities.length}
        </span>
      </div>

      <div className="activities-list">
        {currentActivity && (
          <div 
            className={`activity-card ${isCurrentActivityCompleted ? 'completed' : ''}`}
          >
            <div className="activity-header">
              <div className="activity-icon">
                {getActivityIcon(currentActivity.type)}
              </div>
              <div className="activity-info">
                <h3>{currentActivity.title}</h3>
                <p className="activity-description">{currentActivity.description}</p>
                <span className="activity-time">{currentActivity.estimatedTime}</span>
              </div>
              {isCurrentActivityCompleted && (
                <div className="activity-completed-badge">
                  <span>✓ Completed</span>
                </div>
              )}
            </div>
            
            <div className="activity-content">
              <div className="content-text">
                {currentActivity.content.split('\n').map((line, lineIndex) => {
                  const trimmedLine = line.trim();
                  
                  // Skip empty lines
                  if (!trimmedLine) {
                    return <br key={lineIndex} />;
                  }
                  
                  // Bold headers (lines starting with **)
                  if (trimmedLine.startsWith('**') && trimmedLine.endsWith('**')) {
                    return (
                      <p key={lineIndex} className="bold-text">
                        {trimmedLine.replace(/\*\*/g, '')}
                      </p>
                    );
                  }
                  
                  // Questions/Reflections - add text input
                  if (isQuestion(trimmedLine)) {
                    const questionKey = `${currentActivity.id}_q${lineIndex}`;
                    const questionText = trimmedLine.startsWith('-') 
                      ? trimmedLine.substring(1).trim() 
                      : trimmedLine;
                    
                    return (
                      <div key={lineIndex} className="reflection-question">
                        <label className="reflection-question-label">{questionText}</label>
                        <textarea
                          className="reflection-textarea"
                          placeholder="Write your thoughts here..."
                          value={reflectionAnswers[questionKey] || ''}
                          onChange={(e) => handleReflectionChange(questionKey, e.target.value)}
                        />
                      </div>
                    );
                  }
                  
                  // Regular paragraphs
                  return (
                    <p key={lineIndex}>
                      {trimmedLine}
                    </p>
                  );
                })}
              </div>
              
              <div className="activity-completion-section">
                {!isCurrentActivityCompleted ? (
                  <button 
                    className="btn btn-complete-activity"
                    onClick={() => toggleActivity(currentActivity.id)}
                  >
                    ✓ Mark as Complete
                  </button>
                ) : (
                  <div className="activity-completed-actions">
                    <div className="completion-message">
                      <span className="completion-icon">✓</span>
                      <span>Activity completed!</span>
                    </div>
                    {canGoNext && (
                      <button 
                        className="btn btn-primary btn-continue"
                        onClick={handleNextActivity}
                      >
                        Continue to Next Activity →
                      </button>
                    )}
                    {!canGoNext && allActivitiesCompleted && (
                      <button className="btn btn-primary btn-continue" onClick={onNext}>
                        {t('completeWeek1Start2')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="week-navigation">
        {canGoPrevious && (
          <button 
            className="btn btn-secondary btn-back-activity" 
            onClick={handlePreviousActivity}
          >
            ← Previous Activity
          </button>
        )}
      </div>
    </div>
  );
};

export default WeekOne;